import React, { useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import useReviewLabels from 'hooks/useReviewLabels'
import RadioInput from 'LuxKit/Radio/RadioInput'
import Modal from 'LuxKit/Modal/Modal'
import AssistChip from 'LuxKit/Chips/AssistChip'
import useToggle from 'hooks/useToggle'
import Group from 'components/utils/Group'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import styled from 'styled-components'
import { rem } from 'polished'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Breakpoint from 'components/Common/Breakpoint'

const RadioGroup = styled.div`
  padding-inline: ${rem(16)};
`

interface SortOption {
  label: string
  value: string
}

interface Props {
  sortValue: string
  sortOptions: Array<SortOption>
  onSelect: (option: string) => void
}

function UserReviewSort(props: Props) {
  const { sortValue, sortOptions, onSelect } = props
  const intl = useIntl()
  const { SORT_OPTION_MOST_RELEVANT } = useReviewLabels()

  const [isOpenSort, , openSort, onClose] = useToggle()

  const currentOption = sortOptions.find(option => option.value === sortValue) ?? SORT_OPTION_MOST_RELEVANT

  const sortByChipLabel = intl.formatMessage(
    {
      id: 'AeyBDI++sz',
      defaultMessage: 'Sort by: {sortLabel}',
      description: 'Chip label showing the currently selected review sort option',
    },
    { sortLabel: currentOption.label },
  )

  const sortByModalTitle = intl.formatMessage({
    id: 'eKUk8CBcAX',
    defaultMessage: 'Sort by',
    description: 'Modal title for review sort options on mobile on the offer detail page',
  })

  const onChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>((e) => {
    const value = e.currentTarget.value
    const selected = sortOptions.find(option => option.value === value)

    onClose()
    if (selected) {
      onSelect(selected.value)
    }
  }, [onClose, onSelect, sortOptions])

  const sortInputs = <Group direction="vertical" gap={16}>
    {sortOptions.map(option => <RadioInput
      key={option.value}
      checked={currentOption.value === option.value}
      onChange={onChange}
      name={option.label}
      value={option.value}
      data-testid={`sort-option-${option.value}`}
    >
      {option.label}
    </RadioInput>)}
  </Group>

  const triggerRef = useRef<HTMLButtonElement>(null)

  return (
    <>
      <AssistChip ref={triggerRef} size="medium" variant="filled" onClick={openSort}>
        {sortByChipLabel}
      </AssistChip>
      <CSSBreakpoint min="tablet">
        <DropdownList
          data-testid="sort-options-dropdown"
          size="S"
          open={isOpenSort}
          onClose={onClose}
          anchorRef={triggerRef}
          triggerRef={triggerRef}
        >
          <RadioGroup>
            {sortInputs}
          </RadioGroup>
        </DropdownList>
      </CSSBreakpoint>
      <Breakpoint max="mobile">
        <Modal title={sortByModalTitle} onClose={onClose} isOpen={isOpenSort}>
          {sortInputs}
        </Modal>
      </Breakpoint>
    </>
  )
}

export default UserReviewSort
